"use client";

import type { z } from "zod/v4";
import { ApiError } from "../api-client";
import { getClientToken, notifySessionExpired, refreshClientToken } from "../auth";

export const CLUB_API_URL = process.env.NEXT_PUBLIC_CLUB_API_URL ?? "http://localhost:8080";

// `new Headers(init)` accepts a Headers instance, a tuple list, or a record;
// spreading a Headers instance yields {} and silently drops every header.
function authHeaders(init?: HeadersInit): Headers {
	const headers = new Headers(init);
	if (!headers.has("Content-Type")) {
		headers.set("Content-Type", "application/json");
	}
	const token = getClientToken();
	if (token && !headers.has("Authorization")) {
		headers.set("Authorization", `Bearer ${token}`);
	}
	return headers;
}

async function fetchWithRetry(url: string, options?: RequestInit): Promise<Response> {
	const res = await fetch(url, {
		...options,
		headers: authHeaders(options?.headers),
	});

	if (res.status !== 401) return res;

	const newToken = await refreshClientToken();
	const retried = newToken
		? await fetch(url, {
				...options,
				headers: authHeaders(options?.headers),
			})
		: res;
	if (retried.status === 401) notifySessionExpired();
	return retried;
}

export async function fetchApi<T>(
	path: string,
	schema: z.ZodType<T>,
	options?: RequestInit,
): Promise<T> {
	const res = await fetchWithRetry(`${CLUB_API_URL}${path}`, options);

	if (!res.ok) {
		const body = await res.text();
		throw new ApiError(res.status, body);
	}

	const json = await res.json();
	return schema.parse(json);
}

export async function fetchVoid(path: string, options?: RequestInit): Promise<void> {
	const res = await fetchWithRetry(`${CLUB_API_URL}${path}`, options);

	if (!res.ok) {
		const body = await res.text();
		throw new ApiError(res.status, body);
	}
}

function uploadHeaders(): Record<string, string> {
	const headers: Record<string, string> = {};
	const token = getClientToken();
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}
	return headers;
}

export async function fetchUpload<T>(
	path: string,
	schema: z.ZodType<T>,
	body: FormData,
): Promise<T> {
	const url = `${CLUB_API_URL}${path}`;
	let res = await fetch(url, {
		method: "POST",
		headers: uploadHeaders(),
		body,
	});

	if (res.status === 401) {
		const newToken = await refreshClientToken();
		if (newToken) {
			res = await fetch(url, {
				method: "POST",
				headers: uploadHeaders(),
				body,
			});
		}
	}
	if (res.status === 401) notifySessionExpired();

	if (!res.ok) {
		const text = await res.text();
		throw new ApiError(res.status, text);
	}

	const json = await res.json();
	return schema.parse(json);
}

const BEATS_PATH = "/beats";
const BEATS_SURFACE = "club";

/**
 * Fire-and-forget activity beat endpoint.
 * Silently swallows all errors — no blocking, no retries, no exceptions thrown.
 */
export async function recordActivityBeat(): Promise<void> {
	const token = getClientToken();
	if (!token) {
		return;
	}

	try {
		await fetch(`${CLUB_API_URL}${BEATS_PATH}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ surface: BEATS_SURFACE }),
		});
	} catch {
		// Silently ignore all errors
	}
}
